import {useState, useCallback} from 'react';
import {Stack} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {useParams} from 'react-router-dom';
import {useSnackBar} from '../../contexts/snack-bar/context';
import {useExportDataWithErrorHandler} from '../../hooks/use-export-data-with-error-handler';
import {translations} from '../../i18n/translations';
import {PutCareService} from '../../backend/requests/put-care-service';
import {useCareServicesDates} from './base/data/use-care-services-dates';
import {useCareServicesData} from './base/data/use-care-services-data';
import {CareServicesEditModal} from './base/modals/care-services-edit-modal';
import {CareServicesCreateModal} from './base/modals/care-services-create-modal';
import {CaregiverCareServicesHeader} from './modes/caregiver/caregiver-care-services-header';
import {SeniorsCareServicesHeader} from './modes/seniors/seniors-care-services-header';
import {SeniorCareServicesHeader} from './modes/senior/senior-care-services-header';
import {CaregiverCareServicesBody} from './modes/caregiver/caregiver-care-services-body';
import {SeniorsCareServicesBody} from './modes/seniors/seniors-care-services-body';
import {SeniorCareServicesBody} from './modes/senior/senior-care-services-body';
import {useCareServicePermissions} from './base/data/use-care-service-permissions';
import {handleCareServiceEditError} from './base/data/care-services-handle-error';
import {
  CareServicePreparedExportData,
  CareServiceCreatingItem,
  CareServiceItem,
  CareServicesViewMode,
  CareServicePreparedPdfData,
} from './base/data/care-service-item';
import {
  createPutCareServiceViaForm,
  createPutCareServiceViaItem,
} from './base/data/care-service-data-transformation';
import {
  ConfirmationType,
  CareServicesEditConfirmationModal,
} from './base/modals/care-services-confirmation-modals';
import './care-services.scss';
import {
  prepareCareServicesCalendarPDF,
  prepareCareServicesTablePDF,
} from '../../utils/data/export-data/care-services/export-data-care-services-pdf';
import {prepareCareServicesXLSX} from '../../utils/data/export-data/care-services/export-data-care-services-xlsx';

type CareServicesViewProps = {
  mode: CareServicesViewMode;
};

export function CareServicesView(props: CareServicesViewProps) {
  const {entityId, id} = useParams<{entityId: string; id: string}>();
  const snackbar = useSnackBar();
  const {t} = useTranslation();
  const {previewPdfWithErrorHandling, previewXlsxWithErrorHandling} =
    useExportDataWithErrorHandler();
  // --------- Item selection state ---------
  const [selectedItem, setSelectedItem] = useState<CareServiceItem | undefined>(undefined);
  const [creatingNewItem, setCreatingNewItem] = useState<CareServiceCreatingItem | undefined>(
    undefined
  );
  const [quickSaveConfirmation, setQuickSaveConfirmation] = useState<ConfirmationType>(null);
  // --------- Manage dates state ---------
  const {rangeMode, startDate, endDate, updateSearchOptions} = useCareServicesDates(props.mode);
  // --------- Permissions ---------
  const canWrite = useCareServicePermissions(props.mode);
  // --------- Manage data state (get, post, put..) ---------
  const data = useCareServicesData({
    userId: id,
    entityId,
    endDate: endDate,
    startDate: startDate,
    range: rangeMode,
    mode: props.mode,
  });

  const handleEditItem = useCallback(
    async (item: CareServiceItem, bodyData: PutCareService, refreshOnError?: boolean) => {
      try {
        await data.put(item, bodyData);
        snackbar.setOfficeToastNotification({
          severity: 'success',
          title: t(translations.care_services.toasts.edit.success.title),
          message: t(translations.care_services.toasts.edit.success.message),
        });
      } catch (error) {
        const errorData = handleCareServiceEditError(error);
        snackbar.setOfficeToastNotification({
          severity: 'error',
          title: t(errorData.title),
          message: t(errorData.message),
        });

        if (refreshOnError) data.refresh();

        throw error; // bubble up
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data.put, t]
  );

  const handlingEditItem = useCallback(
    async (item: CareServiceItem | undefined) => {
      if (!item) throw new Error('Care Service Item not found');

      // Multiple instances edit
      if (item.repetition) {
        setSelectedItem(item);
        setQuickSaveConfirmation('repetition');
      }
      // Single instance edit
      else {
        const bodyData = createPutCareServiceViaItem(item, 'only-this-instance');
        await handleEditItem(item, bodyData, true).catch(() => {
          // do nothing. error already handled in handleEditItem
        });
      }
    },
    [handleEditItem]
  );

  const handleClickItem = useCallback((item: CareServiceItem | undefined) => {
    setSelectedItem(item);
    if (!item) console.error('Care Service Item not found');
  }, []);

  const handleCreatingItem = useCallback((item: CareServiceCreatingItem | undefined) => {
    setCreatingNewItem(item);
  }, []);

  const handleExportPdf = useCallback(
    async (preparedData: CareServicePreparedPdfData) => {
      if (props.mode === 'seniors' && (rangeMode === '1m' || rangeMode === '30d')) {
        snackbar.setOfficeToastNotification({
          severity: 'warning',
          message: t(translations.export.error.pdf_monthly_not_supported),
        });

        return null;
      }

      await previewPdfWithErrorHandling(async () => {
        const pdf =
          props.mode === 'seniors'
            ? await prepareCareServicesTablePDF({
                startDate: preparedData.startDate,
                endDate: preparedData.endDate,
                user: preparedData.user,
                entity: preparedData.entity,
                t,
              })
            : await prepareCareServicesCalendarPDF({
                startDate: preparedData.startDate,
                endDate: preparedData.endDate,
                user: preparedData.user,
                entity: preparedData.entity,
                t,
              });

        return {
          file: pdf.file,
          fileName: pdf.fileName,
        };
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [previewPdfWithErrorHandling, props.mode, rangeMode, t]
  );

  const handleExportXlsx = useCallback(
    async (preparedData: CareServicePreparedExportData) => {
      await previewXlsxWithErrorHandling(() => {
        const xlsx = prepareCareServicesXLSX({
          careServices: data.items,
          user: preparedData.user,
          entity: preparedData.entity,
          usersData: {seniors: data.seniors.items, caregivers: data.caregivers.items},
          t,
        });

        return {
          file: xlsx.file,
          fileName: xlsx.fileName,
        };
      });
    },
    [data.caregivers.items, data.items, data.seniors.items, previewXlsxWithErrorHandling, t]
  );

  return (
    <div className="care-services">
      <Stack
        flexDirection="column"
        flex="1"
        gap="14px"
        width="100%"
        minWidth={props.mode === 'seniors' ? '840px' : '690px'}
      >
        {/* --------- HEADER CONTENT --------- */}
        <>
          {props.mode === 'senior' ? (
            <SeniorCareServicesHeader
              canWrite={canWrite}
              startDate={startDate}
              endDate={endDate}
              onCreateItemClick={(item) => {
                setCreatingNewItem(item);
              }}
              onDateChange={(start, end, range) => {
                data.reset(); // reset data right away to avoid re-renders down the tree with old data.
                updateSearchOptions({startDate: start, endDate: end, range: range});
              }}
              filterUsers={data.caregivers}
              onFilterUserChange={(caregiver) => {
                data.filterDataByCaregiverId(caregiver);
              }}
              onExportPdfClick={handleExportPdf}
              onExportXlsxClick={handleExportXlsx}
            />
          ) : props.mode === 'caregiver' ? (
            <CaregiverCareServicesHeader
              canWrite={canWrite}
              startDate={startDate}
              endDate={endDate}
              onCreateItemClick={(item) => {
                setCreatingNewItem(item);
              }}
              onDateChange={(start, end, range) => {
                data.reset(); // reset data right away to avoid re-renders down the tree with old data.
                updateSearchOptions({startDate: start, endDate: end, range: range});
              }}
              filterUsers={data.seniors}
              onFilterUserChange={(senior) => {
                data.filterDataBySeniorId(senior);
              }}
              onExportPdfClick={handleExportPdf}
              onExportXlsxClick={handleExportXlsx}
            />
          ) : props.mode === 'seniors' ? (
            <SeniorsCareServicesHeader
              canWrite={canWrite}
              startDate={startDate}
              endDate={endDate}
              onCreateItemClick={(item) => {
                setCreatingNewItem(item);
              }}
              onDateChange={(start, end, range) => {
                data.reset(); // reset data right away to avoid re-renders down the tree with old data.
                updateSearchOptions({startDate: start, endDate: end, range: range});
              }}
              filterUsers={data.caregivers}
              onFilterUserChange={(caregiver) => {
                data.filterDataByCaregiverId(caregiver);
              }}
              onExportPdfClick={handleExportPdf}
              onExportXlsxClick={handleExportXlsx}
            />
          ) : (
            <></>
          )}
        </>

        {/* --------- BODY CONTENT --------- */}
        <>
          {props.mode === 'senior' ? (
            <SeniorCareServicesBody
              canWrite={canWrite}
              data={data}
              date={startDate}
              range={rangeMode}
              onClickItem={handleClickItem}
              onCreatingItem={handleCreatingItem}
              onEditItem={handlingEditItem}
            />
          ) : props.mode === 'caregiver' ? (
            <CaregiverCareServicesBody
              canWrite={canWrite}
              data={data}
              date={startDate}
              range={rangeMode}
              onClickItem={handleClickItem}
              onCreatingItem={handleCreatingItem}
              onEditItem={handlingEditItem}
            />
          ) : props.mode === 'seniors' ? (
            <SeniorsCareServicesBody
              canWrite={canWrite}
              data={data}
              startDate={startDate}
              endDate={endDate}
              range={rangeMode}
              onClickItem={handleClickItem}
              onCreatingItem={handleCreatingItem}
              onEditItem={handlingEditItem}
            />
          ) : (
            <></>
          )}
        </>
      </Stack>

      {/* ----------- MODALS ----------- */}
      {/* --- EDIT MODAL --- */}
      {selectedItem && !quickSaveConfirmation && (
        <CareServicesEditModal
          open
          item={selectedItem}
          caregivers={data.caregivers}
          seniors={data.seniors}
          canWrite={canWrite}
          onCancel={() => {
            setSelectedItem(undefined);
          }}
          onConfirm={async (form, updateScope) => {
            try {
              const bodyData = createPutCareServiceViaForm(form, updateScope);
              await handleEditItem(selectedItem, bodyData);
              setSelectedItem(undefined);
            } catch {
              // do nothing, error already handled in handleEditItem
            }
          }}
          onDelete={async (deleteOption) => {
            try {
              await data.remove(selectedItem, deleteOption);
              setSelectedItem(undefined);
            } catch (error) {
              console.error('Error deleting item', error);
              snackbar.setOfficeToastNotification({
                severity: 'error',
                title: t(translations.care_services.toasts.delete.error.title),
                message: t(translations.care_services.toasts.delete.error.message),
              });
            }
          }}
        />
      )}

      {/* --- CREATE MODAL --- */}
      {creatingNewItem && (
        <CareServicesCreateModal
          open
          item={creatingNewItem}
          caregivers={data.caregivers}
          seniors={data.seniors}
          onCancel={() => {
            setCreatingNewItem(undefined);
          }}
          onConfirm={async (newItem) => {
            try {
              await data.post(newItem);
              setCreatingNewItem(undefined);
              snackbar.setOfficeToastNotification({
                severity: 'success',
                title: t(translations.care_services.toasts.create.success.title),
                message: t(translations.care_services.toasts.create.success.message),
              });
            } catch (error) {
              console.error('Error creating new item', error);
              snackbar.setOfficeToastNotification({
                severity: 'error',
                title: t(translations.care_services.toasts.create.error.title),
                message: t(translations.care_services.toasts.create.error.message),
              });
            }
          }}
        />
      )}

      {/* --- QUICK(DRAG) EDIT CONFIRMATION MODAL --- */}
      {quickSaveConfirmation && selectedItem && (
        <CareServicesEditConfirmationModal
          confirmationType={quickSaveConfirmation}
          onCancel={() => {
            setQuickSaveConfirmation(null);
            setSelectedItem(undefined);
            data.redraw();
          }}
          onConfirm={async (updateScope) => {
            try {
              const bodyData = createPutCareServiceViaItem(selectedItem, updateScope);
              await handleEditItem(selectedItem, bodyData, true);
            } finally {
              setQuickSaveConfirmation(null);
              setSelectedItem(undefined);
            }
          }}
        />
      )}
    </div>
  );
}
